import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// In-app browsers (Facebook, Instagram, LinkedIn) block blob: URLs and async window.open
function isInAppBrowser() {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|Instagram|LinkedInApp|Twitter|MicroMessenger|Line\//.test(ua);
}

export function ManualTemplateBanner({ href, isLoggedIn = false, onNudge }) {
  const [loading, setLoading] = useState(false);

  async function doDownload() {
    if (loading || !href) return;

    // In-app browsers can't handle blob downloads — open directly
    if (isInAppBrowser()) {
      window.location.href = href;
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(href, { credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rra-template.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the template. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleDownload() {
    if (isLoggedIn) {
      doDownload();
    } else if (onNudge) {
      onNudge(doDownload);
    } else {
      doDownload();
    }
  }

  return (
    <div className="rounded-xl border border-border bg-muted/50 p-4 md:p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground leading-snug">Prefer to do this manually?</p>
        <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">
          We've put together a clean RRA / document delivery receipt template you can download and use for manual record-keeping.
        </p>
      </div>
      <div className="flex flex-col items-start sm:items-end gap-1 shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 whitespace-nowrap"
          onClick={handleDownload}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Download template
        </Button>
        <p className="text-xs text-muted-foreground text-left sm:text-right leading-snug max-w-[220px]">
          Includes delivery method, document list, and tenant acknowledgement in one place.
        </p>
      </div>
    </div>
  );
}
